/**
 * useSwipe — sfogliare col trascinamento (mobile e mouse).
 *
 * Andrea 2026-09-09: le frecce per cambiare classifica in home sono
 * piccole per il pollice, quindi la card si sfoglia anche trascinando.
 *
 * Dettagli che contano:
 *  - la direzione si decide al primo movimento utile: se il dito va piu'
 *    in verticale che in orizzontale lasciamo perdere, e' uno scroll di
 *    pagina (serve `touch-pan-y` sul contenitore);
 *  - dopo un trascinamento il click va annullato, altrimenti una card
 *    dentro un <Link> naviga appena stacchi il dito (`trascinato`);
 *  - `dx` serve solo per far seguire il dito alla card (feedback).
 */

import { useRef, useState } from "react";

/** Spostamento minimo per cambiare pagina. */
const SOGLIA_CAMBIO = 45;
/** Sotto questa soglia non si capisce ancora la direzione del gesto. */
const SOGLIA_DIREZIONE = 8;

interface UseSwipeOpts {
  onAvanti: () => void;
  onIndietro: () => void;
  /** false = gesto disattivato (es. una sola pagina da sfogliare). */
  attivo?: boolean;
}

export function useSwipe({ onAvanti, onIndietro, attivo = true }: UseSwipeOpts) {
  const partenza = useRef<{ x: number; y: number } | null>(null);
  const orizzontale = useRef(false);
  const dxRef = useRef(0);
  /** true tra la fine di un trascinamento e il click che ne consegue. */
  const trascinato = useRef(false);
  const [dx, setDx] = useState(0);

  function reset() {
    partenza.current = null;
    orizzontale.current = false;
    dxRef.current = 0;
    setDx(0);
  }

  const handlers = {
    onPointerDown: (e: React.PointerEvent) => {
      trascinato.current = false;
      if (!attivo || e.button !== 0) return;
      partenza.current = { x: e.clientX, y: e.clientY };
      orizzontale.current = false;
      dxRef.current = 0;
    },
    onPointerMove: (e: React.PointerEvent) => {
      const p = partenza.current;
      if (!p) return;
      const ddx = e.clientX - p.x;
      const ddy = e.clientY - p.y;
      if (!orizzontale.current) {
        if (
          Math.abs(ddx) < SOGLIA_DIREZIONE &&
          Math.abs(ddy) < SOGLIA_DIREZIONE
        )
          return;
        if (Math.abs(ddy) >= Math.abs(ddx)) {
          // Scroll verticale: il gesto non e' nostro.
          reset();
          return;
        }
        orizzontale.current = true;
      }
      dxRef.current = ddx;
      setDx(ddx);
    },
    onPointerUp: () => {
      const eraOrizzontale = orizzontale.current;
      const spostamento = dxRef.current;
      reset();
      if (!eraOrizzontale) return;
      trascinato.current = true;
      if (spostamento <= -SOGLIA_CAMBIO) onAvanti();
      else if (spostamento >= SOGLIA_CAMBIO) onIndietro();
    },
    onPointerCancel: reset,
    onPointerLeave: reset,
  };

  return {
    handlers,
    /** Offset da applicare alla card (smorzato) mentre si trascina. */
    offset: orizzontale.current ? Math.max(-70, Math.min(70, dx * 0.5)) : 0,
    inTrascinamento: orizzontale.current,
    /** Da chiamare nell'onClick per non far scattare i link dopo un gesto. */
    annullaClick: (e: React.MouseEvent) => {
      if (trascinato.current) {
        e.preventDefault();
        e.stopPropagation();
        trascinato.current = false;
      }
    },
  };
}
